import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { Dialog, Spinner, Toast } from './components/ui.jsx';
import { Login } from './screens/Login.jsx';
import { Leader } from './screens/Leader.jsx';
import { Admin } from './screens/Admin.jsx';

/** The season to open: this calendar year if it exists, otherwise the latest. */
function pickYear(years, current) {
  if (current != null && years.includes(current)) return current;
  const now = new Date().getFullYear();
  if (years.includes(now)) return now;
  return years.length ? years[years.length - 1] : null;
}

export function App() {
  const [session, setSession] = useState(undefined);   // undefined = still probing
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const [dialog, setDialog] = useState(null);

  /* Open a modal dialog; resolves true / the typed value, or null on cancel. */
  const dialogRef = useRef(null);
  const ask = useCallback(opts => new Promise(resolve => {
    dialogRef.current?.resolve(null);   // a dialog already open counts as cancelled
    dialogRef.current = { ...opts, resolve };
    setDialog(dialogRef.current);
  }), []);
  const closeDialog = useCallback(result => {
    dialogRef.current?.resolve(result);
    dialogRef.current = null;
    setDialog(null);
  }, []);

  const notify = useCallback((message, error = false) => {
    clearTimeout(toastTimer.current);
    setToast({ message, error });
    toastTimer.current = setTimeout(() => setToast(null), error ? 5000 : 2200);
  }, []);

  const load = useCallback(async () => {
    const d = await api.get('/state');
    setData(d);
    setYear(y => pickYear(d.years, y));
  }, []);

  useEffect(() => {
    api.get('/auth/session')
      .then(async s => {
        if (s) await load();
        setSession(s);
      })
      .catch(() => setSession(null));
  }, [load]);

  const actions = useMemo(() => {
    const signedOut = () => { setSession(null); setData(null); };

    /** Run an API call; errors become a toast, an expired session goes back to login. */
    const run = async (fn, { ok, reloadOnError } = {}) => {
      try {
        const result = await fn();
        if (ok) notify(ok);
        return result;
      } catch (err) {
        if (err.status === 401) { signedOut(); notify('Du har loggats ut. Logga in igen.', true); return undefined; }
        notify(err.message || 'Något gick fel.', true);
        if (reloadOnError) load().catch(() => {});
        return undefined;
      }
    };

    const patchSeason = (y, fn) => setData(d => (d && d.seasons[y]
      ? { ...d, seasons: { ...d.seasons, [y]: fn(d.seasons[y]) } }
      : d));

    const patchTeam = (y, teamId, fn) => patchSeason(y, s => ({
      ...s, teams: s.teams.map(t => (t.id === teamId ? fn(t) : t)),
    }));

    const setTeam = (y, team) => patchSeason(y, s => ({
      ...s,
      teams: s.teams.some(t => t.id === team.id)
        ? s.teams.map(t => (t.id === team.id ? team : t))
        : [...s.teams, team],   // a new team is placed last in the admin's order
    }));

    return {
      notify, run, load,
      confirm: opts => ask(opts).then(Boolean),
      prompt: opts => ask(opts),
      patchSeason, patchTeam, setTeam,

      /* credentials: {role, name, password} for admin, {role, teamId, password} for ledare */
      async login(credentials) {
        const s = await api.post('/auth/login', credentials);
        await load();
        setSession(s);
      },

      async logout() {
        await api.post('/auth/logout').catch(() => {});
        signedOut();
      },

      /* Optimistic: the number shows at once, the server's copy replaces it. */
      saveSold(y, teamId, playerId, type, value) {
        patchTeam(y, teamId, t => ({
          ...t,
          players: t.players.map(p => (p.id === playerId ? { ...p, sold: { ...p.sold, [type]: value } } : p)),
        }));
        return run(async () => {
          const team = await api.put(`/seasons/${y}/teams/${teamId}/players/${playerId}/sold`, { type, sold: value });
          setTeam(y, team);
        }, { reloadOnError: true });
      },

      /* Optimistic tick of a player's `paid` flag. */
      savePlayer(y, teamId, playerId, flags) {
        patchTeam(y, teamId, t => ({
          ...t,
          players: t.players.map(p => (p.id === playerId ? { ...p, ...flags } : p)),
        }));
        return run(async () => {
          setTeam(y, await api.patch(`/seasons/${y}/teams/${teamId}/players/${playerId}`, flags));
        }, { reloadOnError: true });
      },

      setDone(y, teamId, done) {
        patchTeam(y, teamId, t => ({ ...t, done }));
        return run(async () => {
          setTeam(y, await api.put(`/seasons/${y}/teams/${teamId}/done`, { done }));
        }, { reloadOnError: true, ok: done ? 'Laget är markerat som klart.' : undefined });
      },
    };
  }, [notify, load, ask]);

  let screen;
  if (session === undefined) screen = <Spinner label="Laddar…" />;
  else if (!session) screen = <Login onLogin={actions.login} />;
  else if (!data) screen = <Spinner label="Hämtar säsonger…" />;
  else if (session.role === 'admin') {
    screen = <Admin data={data} year={year} setYear={setYear} actions={actions} />;
  } else {
    screen = <Leader data={data} year={year} setYear={setYear} session={session} actions={actions} />;
  }

  return <>{screen}<Dialog dialog={dialog} onClose={closeDialog} /><Toast toast={toast} /></>;
}
